import React, { CSSProperties, ReactNode } from "react";
import { CheckIcon, EsgAnomalyWarning, InfoIcon, LockedIcon } from "assets";

export type StatusBannerVariant =
  | "info"
  | "warning"
  | "caution"
  | "success"
  | "danger";

export interface StatusBannerProps {
  /** Visual variant — controls color and default icon */
  variant: StatusBannerVariant;
  /** Override the default icon. Pass a ReactNode, or null to render no icon at all. */
  icon?: ReactNode | null;
  /** Main content. Can be a string or any ReactNode for rich markup. */
  children: ReactNode;
  /** Extra class names applied to the root element */
  className?: string;
  /** Inline styles applied to the root element */
  style?: CSSProperties;
}

const ICON_SIZE = 22;

const VARIANT_STYLES: Record<
  StatusBannerVariant,
  { background: string; border: string; color: string }
> = {
  info: { background: "#e8f4fd", border: "#a0c8f0", color: "#0a3060" },
  warning: { background: "#fff8e1", border: "#e8b800", color: "#5a3800" },
  caution: { background: "#fff3e0", border: "#e88a00", color: "#5a2800" },
  success: { background: "#e8f5ee", border: "#40b06a", color: "#0a3d20" },
  danger: { background: "#fdeaea", border: "#e05050", color: "#5a0a0a" },
};

const DEFAULT_ICONS: Record<StatusBannerVariant, ReactNode> = {
  info: <InfoIcon width={ICON_SIZE} height={ICON_SIZE} />,
  warning: <EsgAnomalyWarning width={ICON_SIZE} height={ICON_SIZE} />,
  caution: <LockedIcon width={ICON_SIZE} height={ICON_SIZE} />,
  success: <CheckIcon width={ICON_SIZE} height={ICON_SIZE} />,
  danger: <EsgAnomalyWarning width={ICON_SIZE} height={ICON_SIZE} />,
};

export const StatusBanner = ({
  variant,
  icon,
  children,
  className,
  style,
}: StatusBannerProps) => {
  const { background, border, color } = VARIANT_STYLES[variant];
  const resolvedIcon = icon === null ? null : icon ?? DEFAULT_ICONS[variant];

  return (
    <div
      className={className}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "12px 16px",
        borderRadius: "7px",
        border: `1px solid ${border}`,
        backgroundColor: background,
        color,
        fontSize: "12px",
        lineHeight: 1.55,
        ...style,
      }}
    >
      {resolvedIcon && (
        <span
          style={{
            display: "flex",
            flexShrink: 0,
            color,
          }}
        >
          {resolvedIcon}
        </span>
      )}
      <div>{children}</div>
    </div>
  );
};
